import { useEffect, useState, useMemo } from 'react';
import { observer, inject } from 'mobx-react';
import { Box, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { useSortableTableData } from '../../../components/table/hooks/useSortableTableData';
import { statementTablePaperSx, statementTableContainerSx, statementTableSx, statementTableHeaderSx } from '../../../components/table/mui-table-styles';
import { fetch } from '../../../utils/api-wrapper';
import InvoicePreview from '../../../components/Invoice-preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import FormTextField from '../../../components/form-textfield';
import LoadingSpinner from '../../../components/loading-spinner/loading-spinner';
import Modal from '../../../components/modal.js';
import toast from 'react-hot-toast';
import Tooltip from '../../../components/tooltip/tooltip';

const columnNameKeys = {
  expense_preview: 'cc_statements.uncategorized_expenses.column_names.expense_preview',
  expense_date: 'cc_statements.uncategorized_expenses.column_names.date',
  possible_masks: 'cc_statements.uncategorized_expenses.column_names.possible_masks',
  total_amount: 'cc_statements.uncategorized_expenses.column_names.total_amount',
  user: 'cc_statements.uncategorized_expenses.column_names.user',
  explanation: 'cc_statements.uncategorized_expenses.column_names.explanation',
  restart_matching: 'cc_statements.uncategorized_expenses.column_names.restart_matching',
};

const UncategorizedExpensesTable = ({ data, refreshStatementData, filteredMask = '' }) => {
  const [uncategorizedExpenses, setUncategorizedExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fieldsAreUpdating, setFieldsAreUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteExpenseConfirmation, setShowDeleteExpenseConfirmation] = useState(false);

  const prepareTableData = (expenses) => {
    const tableData = expenses.map((expense) => ({
      // Provide a stable identifier so selection comparison can rely on _id
      _id: expense?._id,
      expense_preview: <InvoicePreview key={`${expense?.checksum}-${expense?.json?.Name}`} file_name={expense?.json?.Name || expense.file_name} currentInvoice={null} />,
      expense_date: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='date'
          value={expense?.json?.InvoiceDate || ''}
          validateChange={(val) => {
            updateExpense({ ...expense, json: { ...expense?.json, InvoiceDate: val } });
          }}
        />
      ),
      possible_masks: (expense?.user?.cards?.map((card) => `${card.card_masked_number}`) || []).join(', '),
      total_amount: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='money'
          value={expense?.json?.TotalWithTax ? `${expense?.json.TotalWithTax.toFixed(2)}` : '0.00'}
          validateChange={(val) => {
            const parsed = Number.parseFloat(val);
            if (!Number.isFinite(parsed)) return; // ignore invalid input
            updateExpense({ ...expense, json: { ...expense?.json, TotalWithTax: parsed } });
          }}
        />
      ),
      user: (() => {
        const first = expense?.user?.first_name?.trim();
        const last = expense?.user?.last_name?.trim();
        const combined = [first, last].filter(Boolean).join(' ').trim();
        return combined || 'N/A';
      })(),
      explanation: (
        <Tooltip message={expense?.reason || 'N/A'}>
          <div className='text-truncate'>{expense?.reason || 'N/A'}</div>
        </Tooltip>
      ),
      restart_matching: (
        <button
          className='btn btn-sm btn-primary w-100 d-flex justify-content-center align-items-center'
          disabled={expense?.__restarting}
          onClick={() => restartMatching(expense?.uncategorized_expense_id)}
        >
          {expense?.__restarting ? (
            <LoadingSpinner data-testid={`restart-spinner-${expense?._id}`} />
          ) : (
            <>
              <FontAwesomeIcon icon={faSync} className='mr-2' />
              {data.t.read('cc_statements.uncategorized_expenses.column_names.restart_matching')}
            </>
          )}
        </button>
      ),
      raw_expense: expense,
    }));

    if (filteredMask && filteredMask !== 'all') {
      return tableData.filter((row) => row.possible_masks.includes(filteredMask));
    }

    return tableData;
  };

  useEffect(() => {
    fetchUncategorizedExpenses();
  }, []);

  const fetchUncategorizedExpenses = async () => {
    setLoading(true);
    fetch(
      '/expenses/uncategorized',
      'POST',
      {},
      (response) => {
        const uncategorizedExpenses = response?.data?.uncategorized_expenses || [];
        setUncategorizedExpenses(uncategorizedExpenses);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching uncategorized expenses:', error);
        setLoading(false);
      },
      true
    );
  };

  const restartMatching = async (expenseId) => {
    // Mark expense as restarting
    setUncategorizedExpenses((prev) => prev.map((e) => (e.uncategorized_expense_id === expenseId ? { ...e, __restarting: true } : e)));

    await fetch(
      `/expenses/uncategorized/${expenseId}`,
      'POST',
      {},
      () => {
        fetchUncategorizedExpenses();
        refreshStatementData();
      },
      (error) => {
        console.error('Error restarting matching for expense:', expenseId, error);
        setUncategorizedExpenses((prev) => prev.map((e) => (e.uncategorized_expense_id === expenseId ? { ...e, __restarting: false } : e)));
      },
      true
    );
  };

  const updateExpense = async (updatedExpense) => {
    setFieldsAreUpdating(true);

    // Optimistically update the UI
    const previousSnapshot = uncategorizedExpenses;
    setUncategorizedExpenses((prev) => prev.map((e) => (e._id === updatedExpense._id ? updatedExpense : e)));

    try {
      await fetch(
        '/expenses/uncategorized',
        'PUT',
        updatedExpense,
        () => {},
        (error) => {
          throw error;
        }
      );
    } catch {
      setUncategorizedExpenses(previousSnapshot);
    } finally {
      setFieldsAreUpdating(false);
    }
  };

  const deleteSelectedExpenses = () => {
    const expense_ids = selectedRows.map((exp) => exp.raw_expense.uncategorized_expense_id);
    fetch(
      '/expenses/uncategorized',
      'DELETE',
      { expense_ids },
      (response) => {
        toast.success(data.t.read('cc_transactions.delete_successful') || 'Expenses deleted successfully');
        fetchUncategorizedExpenses();
      },
      () => {
        toast.error(data.t.read('cc_transactions.delete_failed') || 'Failed to delete expenses');
      }
    );
    setShowDeleteExpenseConfirmation(false);
  };

  const memoizedTableData = useMemo(() => prepareTableData(uncategorizedExpenses), [uncategorizedExpenses, fieldsAreUpdating, filteredMask]);

  const { sortedData, sortConfig, handleSort } = useSortableTableData(memoizedTableData);
  const columns = Object.entries(columnNameKeys);
  const selectedRows = memoizedTableData.filter((row) => selectedIds.includes(row._id));
  const allSelected = memoizedTableData.length > 0 && selectedRows.length === memoizedTableData.length;

  // Drop hidden or removed expenses from selection before a later bulk delete.
  useEffect(() => {
    setSelectedIds((ids) => ids.filter((id) => memoizedTableData.some((row) => row._id === id)));
  }, [memoizedTableData]);

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3 mt-5'>
        <Box component='h2' sx={{ m: 0, fontSize: '18px', fontWeight: 500, color: '#20242a' }}>{data.t.read('cc_statements.uncategorized_expenses.title')}</Box>
        {selectedRows && selectedRows.length > 0 && (
          <button className='btn btn-danger' onClick={() => setShowDeleteExpenseConfirmation(true)}>
            <FontAwesomeIcon icon={faTrash} className='mr-2' />
            {data.t.read('cc_transactions.delete_expenses')}
          </button>
        )}
      </div>
      <Paper elevation={0} sx={statementTablePaperSx}>
        <TableContainer sx={statementTableContainerSx}>
          <Table stickyHeader aria-label={data.t.read('cc_statements.uncategorized_expenses.title')} aria-busy={loading} sx={statementTableSx}>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' sx={{ ...statementTableHeaderSx, py: 0 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedRows.length > 0 && !allSelected}
                    disabled={loading || memoizedTableData.length === 0}
                    inputProps={{ 'aria-label': data.t.read('cc_statements.uncategorized_expenses.select_all') }}
                    onChange={(event) => setSelectedIds(event.target.checked ? memoizedTableData.map((row) => row._id) : [])}
                  />
                </TableCell>
                {columns.map(([key, label]) => (
                  <TableCell key={key} sortDirection={sortConfig.key === key ? sortConfig.direction : false} sx={statementTableHeaderSx}>
                    <TableSortLabel
                      active={sortConfig.key === key}
                      direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort(key)}
                    >
                      {data.t.read(label)}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={columns.length + 1}>
                  <div className='d-flex justify-content-center'><LoadingSpinner /></div>
                </TableCell></TableRow>
              ) : sortedData.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length + 1} align='center'>{data.t.read('no_data')}</TableCell></TableRow>
              ) : sortedData.map((row) => (
                <TableRow key={row._id} hover selected={selectedIds.includes(row._id)} data-testid='table-row'>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={selectedIds.includes(row._id)}
                      inputProps={{ 'aria-label': `${data.t.read('select')} ${row.raw_expense.json?.Name || row.raw_expense.file_name || row._id}` }}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setSelectedIds((ids) => checked ? [...ids, row._id] : ids.filter((id) => id !== row._id));
                      }}
                    />
                  </TableCell>
                  {columns.map(([key]) => <TableCell key={key} data-col={key}>{row[key]}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {showDeleteExpenseConfirmation && (
        <Modal
          width='50vw'
          title={data.t.read('cc_transactions.delete_expenses')}
          cancelAction={() => setShowDeleteExpenseConfirmation(false)}
          okAction={deleteSelectedExpenses}
          okText={data.t.read('delete')}
          cancelText={data.t.read('cancel')}
        >
          <p>{`${data.t.read('cc_transactions.delete_expenses_confirm')} (${selectedRows.length})${data.t.read(
            'cc_transactions.delete_expenses_confirm2'
          )}`}</p>
        </Modal>
      )}
    </div>
  );
};

export default inject('data')(observer(UncategorizedExpensesTable));
